import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { Invoice } from "../../models";
import {
  getInvoiceAccountingStatus,
  type InvoiceAccountingStatus,
} from "../../helpers/invoiceFilters";
import { StatusBadge } from "./StatusBadge";

interface InvoiceAccountingStatusBadgeProps {
  invoice: Invoice;
}

const presentation: Record<
  Exclude<InvoiceAccountingStatus, "not_registered" | "not_applicable">,
  { translationKey: string; bgStyle: string }
> = {
  manual_registered: {
    translationKey: "invoice:accounting_status.manual_registered.status",
    bgStyle: "bg-success-muted text-success dark:text-success",
  },
  creating: {
    translationKey: "invoice:accounting_status.creating.status",
    bgStyle: "bg-brand-muted text-brand dark:text-brand",
  },
  created: {
    translationKey: "invoice:accounting_status.created.status",
    bgStyle: "bg-success-muted text-success dark:text-success",
  },
  sync_error: {
    translationKey: "invoice:accounting_status.sync_error.status",
    bgStyle: "bg-warning-muted text-lgb-yellow-400",
  },
};

export const InvoiceAccountingStatusBadge: FC<
  InvoiceAccountingStatusBadgeProps
> = ({ invoice }) => {
  const { t } = useTranslation(["invoice"]);
  const status = getInvoiceAccountingStatus(invoice);

  if (status === "not_applicable") {
    return <span className="text-sm text-muted">—</span>;
  }
  if (status === "not_registered") {
    return (
      <span className="text-sm text-muted">
        {t("invoice:accounting_status.not_registered.status")}
      </span>
    );
  }

  const current = presentation[status];
  return (
    <StatusBadge
      status={t(current.translationKey)}
      bgStyle={current.bgStyle}
    />
  );
};
